import React from 'react';
import styled from 'styled-components';
import { StatusBar, type StatusBarItem } from './StatusBar';
import { Breadcrumb, type BreadcrumbItem } from '../components/shared/Breadcrumb';
import { HelpButton } from '../components/HelpPanel';

interface AppShellProps {
    title?: string;
    subtitle?: string;
    breadcrumb?: BreadcrumbItem[];
    onBack?: () => void;
    backLabel?: string;
    actions?: React.ReactNode;
    statusItems?: StatusBarItem[];
    constrainHeight?: boolean;
    /** Opt-in dense chrome for canvas-dominant views (the document inspect
     *  view): tighter container padding and row gap. Views that don't set it
     *  render exactly as before. */
    compact?: boolean;
    onLogoClick?: () => void;
    userInitial?: string;
    jobsIndicator?: React.ReactNode;
    children: React.ReactNode;
}

const AppContainer = styled.div<{ $constrainHeight?: boolean; $compact?: boolean }>`
    font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
    margin: 0;
    padding: ${({ $compact }) => ($compact ? '10px 24px 16px' : '32px 40px')};
    background: #fefefe;
    ${({ $constrainHeight }) => $constrainHeight
        ? 'height: 100vh; overflow: hidden;'
        : 'min-height: 100vh;'
    }
    display: flex;
    flex-direction: column;
`;

const Layout = styled.div<{ $compact?: boolean }>`
    max-width: 1100px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    width: 100%;
    gap: ${({ $compact }) => ($compact ? '8px' : '24px')};
`;

/* ── App bar ───────────────────────────────────────────────── */

/* Compact by design (~30px tall): the bar reads as chrome, not a header. */
const AppBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 6px;
    border-bottom: 1px solid #f0f0f0;
`;

const LogoLink = styled.button`
    display: flex;
    align-items: center;
    gap: 10px;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    font-family: inherit;
`;

const LogoIcon = styled.div`
    width: 22px;
    height: 22px;
    border-radius: 5px;
    background: #111;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 12px;
    font-weight: 700;
`;

const LogoText = styled.span`
    font-size: 14px;
    font-weight: 600;
    letter-spacing: -0.02em;
    color: #111;
`;

const AppBarRight = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const UserAvatar = styled.div`
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #f0f0f0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 600;
    color: #666;
`;

/* ── Page header ───────────────────────────────────────────── */

const HeaderSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const HeaderRow = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 16px;
`;

const HeaderContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const Title = styled.h1`
    color: #111;
    font-size: 22px;
    font-weight: 600;
    margin: 0;
    letter-spacing: -0.02em;
`;

const Subtitle = styled.p`
    margin: 0;
    font-size: 13px;
    color: #999;
`;

export const AppShell: React.FC<AppShellProps> = ({
    title,
    subtitle,
    breadcrumb,
    onBack,
    backLabel,
    actions,
    statusItems,
    constrainHeight,
    compact,
    onLogoClick,
    userInitial,
    jobsIndicator,
    children,
}) => {
    const effectiveBreadcrumb = breadcrumb ?? (onBack
        ? [{ label: backLabel ?? 'Back', onClick: onBack }, ...(title ? [{ label: title }] : [])]
        : undefined
    );

    // Derive logo click from breadcrumb "Cases" segment or onBack
    const handleLogoClick = onLogoClick
        ?? effectiveBreadcrumb?.find(item => item.label === 'Cases')?.onClick
        ?? onBack
        ?? undefined;

    const showHeader = effectiveBreadcrumb || title || subtitle || actions;

    return (
        <AppContainer $constrainHeight={constrainHeight} $compact={compact}>
            <Layout $compact={compact}>
                <AppBar>
                    <LogoLink type="button" onClick={handleLogoClick} aria-label="Go to Cases">
                        <LogoIcon>B</LogoIcon>
                        <LogoText>BigPDF</LogoText>
                    </LogoLink>
                    <AppBarRight>
                        {jobsIndicator}
                        <HelpButton />
                        {userInitial && <UserAvatar>{userInitial}</UserAvatar>}
                    </AppBarRight>
                </AppBar>

                {showHeader && (
                    <HeaderSection>
                        {effectiveBreadcrumb && <Breadcrumb items={effectiveBreadcrumb} />}
                        <HeaderRow>
                            <HeaderContent>
                                {title && <Title>{title}</Title>}
                                {subtitle && <Subtitle>{subtitle}</Subtitle>}
                            </HeaderContent>
                            {actions}
                        </HeaderRow>
                    </HeaderSection>
                )}
                {children}
            </Layout>
            {statusItems && statusItems.length > 0 && <StatusBar items={statusItems} />}
        </AppContainer>
    );
};
